import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Card,
  Select,
  Button,
  Input,
  Row,
  Col,
  message,
  Spin,
  Typography,
  Space,
  TimePicker,
} from 'antd';
import Table from 'components/Common/Table';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as adminactions from '../../store/actions/AuthActions';
import useUrlFilters from 'hooks/useUrlFilters';

const { Title, Text } = Typography;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Period lives in the URL so the view can be shared as a link. No schema
// defaults: the fallback is "now", and a link must keep the period it was made for.
const URL_FILTERS = {
  month: { type: 'string' },
  year: { type: 'number' },
};

const AttendanceManager = () => {
  const organization = useSelector((state) => state.auth.organization);
  const userPermissions = useSelector((state) => state.auth.user?.user_permissions || []);
  const hasUpdate = userPermissions.includes('attendance:update');

  // Filters
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const selectedMonth = MONTHS.includes(filters.month) ? filters.month : MONTHS[new Date().getMonth()];
  const setSelectedMonth = (value) => setFilters({ month: value });
  const selectedYear = filters.year || new Date().getFullYear();
  const setSelectedYear = (value) => setFilters({ year: value });

  // Data
  const [employees, setEmployees] = useState([]);
  const [payrollDays, setPayrollDays] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [attendanceCounts, setAttendanceCounts] = useState({});

  // Selected cell
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [presentEmployees, setPresentEmployees] = useState([]);
  const [absentEmployees, setAbsentEmployees] = useState([]);
  const [searchPresent, setSearchPresent] = useState('');
  const [searchAbsent, setSearchAbsent] = useState('');

  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingDay, setLoadingDay] = useState(false);

  // Generate days for the selected month
  useEffect(() => {
    if (organization) {
      generatePayrollDays();
    }
  }, [selectedMonth, selectedYear, organization]);

  const generatePayrollDays = () => {
    const monthIdx = MONTHS.indexOf(selectedMonth) + 1;
    const daysInMonth = new Date(selectedYear, monthIdx, 0).getDate();
    const days = [];

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, month: monthIdx, year: selectedYear });
    }

    setPayrollDays(days);
    setStartDate(`1/${monthIdx}/${selectedYear}`);
    setEndDate(`${daysInMonth}/${monthIdx}/${selectedYear}`);
    setPresentEmployees([]);
    setAbsentEmployees([]);
    setSelectedDate('');
    setSelectedCell(null);
  };

  // Load employees for the organization
  useEffect(() => {
    const loadEmployees = async () => {
      if (!organization) return;

      try {
        const response = await adminactions.loadEmployees(1, 1000, organization);
        if (response && response.results) {
          setEmployees(response.results);
        }
      } catch (error) {
        console.error('Error loading employees:', error);
      }
    };

    loadEmployees();
  }, [organization]);

  // Fetch attendance counts for the period
  const fetchAttendanceCounts = async () => {
    if (!organization || payrollDays.length === 0) return;

    setLoading(true);
    try {
      const monthIdx = MONTHS.indexOf(selectedMonth) + 1;
      const startDateStr = `${selectedYear}-${String(monthIdx).padStart(2, '0')}-01`;
      const endDateStr = `${selectedYear}-${String(monthIdx).padStart(2, '0')}-${String(payrollDays.length).padStart(2, '0')}`;

      const response = await adminactions.loadAttendanceRecords(1, 10000, organization, {
        date_from: startDateStr,
        date_to: endDateStr,
      });

      if (response && response.results) {
        const counts = {};

        response.results.forEach((record) => {
          const date = new Date(record.date);
          const day = date.getDate();
          const month = date.getMonth() + 1;
          const year = date.getFullYear();
          const empId = record.employee?.id;

          const key = `${empId}-${day}-${month}-${year}`;
          counts[key] = (counts[key] || 0) + 1;
        });

        setAttendanceCounts(counts);
        message.success('Calendar loaded');
      }
    } catch (error) {
      message.error('Failed to load attendance counts');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle cell click to show day details
  const handleCellClick = async (empId, empName, day, month, year) => {
    setSelectedCell({ empId, day, month, year });
    setSelectedDate(`${day}/${month}/${year}`);
    setLoadingDay(true);

    try {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

      const response = await adminactions.loadAttendanceRecords(1, 1000, organization, {
        date_from: dateStr,
        date_to: dateStr,
        employee: empId,
      });

      if (response && response.results && response.results.length > 0) {
        const record = response.results[0];
        setPresentEmployees([{
          id: record.id,
          emp_id: record.employee?.employee_id || empId,
          name: empName,
          first_in: record.first_in || '',
          last_out: record.last_out || '',
          total_hours_worked: record.total_hours_worked || 0,
          status: record.status || '',
        }]);
        setAbsentEmployees([]);
      } else {
        // Employee was absent
        setPresentEmployees([]);
        setAbsentEmployees([{
          emp_id: employees.find(e => e.id === empId)?.employee_id || empId,
          name: empName,
        }]);
      }
    } catch (error) {
      message.error('Failed to load attendance details');
      console.error('Error:', error);
    } finally {
      setLoadingDay(false);
    }
  };

  // Update attendance field
  const updateAttendanceField = async (id, field, value) => {
    if (!hasUpdate) {
      message.error('No permission to update');
      return;
    }

    try {
      const data = {};
      if (field === 'first_in') {
        data.first_in = value;
      } else if (field === 'last_out') {
        data.last_out = value;
      } else {
        data[field] = value;
      }

      await adminactions.updateAttendance(id, data);

      setPresentEmployees((prev) =>
        prev.map((emp) => (emp.id === id ? { ...emp, [field]: value } : emp))
      );

      message.success('Updated successfully');
    } catch (error) {
      message.error('Update failed');
      console.error('Error:', error);
    }
  };

  // Filter functions
  const filteredPresent = presentEmployees.filter((emp) =>
    !searchPresent ||
    emp.name?.toLowerCase().includes(searchPresent.toLowerCase()) ||
    emp.emp_id?.toString().includes(searchPresent)
  );

  const filteredAbsent = absentEmployees.filter((emp) =>
    !searchAbsent ||
    emp.name?.toLowerCase().includes(searchAbsent.toLowerCase()) ||
    emp.emp_id?.toString().includes(searchAbsent)
  );

  // Table columns for calendar view
  const calendarColumns = [
    {
      title: 'Employee',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 200,
      render: (text, record) => <strong>{`${record.first_name} ${record.last_name}`}</strong>,
    },
    ...payrollDays.map((d) => ({
      title: d.day,
      key: `day-${d.day}`,
      width: 50,
      align: 'center',
      render: (_, record) => {
        const key = `${record.id}-${d.day}-${d.month}-${d.year}`;
        const count = attendanceCounts[key] || 0;
        const isSelected =
          selectedCell &&
          selectedCell.empId === record.id &&
          selectedCell.day === d.day &&
          selectedCell.month === d.month &&
          selectedCell.year === d.year;

        return (
          <div
            style={{
              cursor: 'pointer',
              backgroundColor: isSelected ? '#ff7a45' : count > 0 ? '#52c41a' : '#f5f5f5',
              color: isSelected || count > 0 ? '#fff' : '#000',
              padding: '4px',
              borderRadius: '4px',
              fontWeight: isSelected ? 'bold' : 'normal',
            }}
            onClick={() => handleCellClick(record.id, `${record.first_name} ${record.last_name}`, d.day, d.month, d.year)}
          >
            {count > 0 ? 'P' : '-'}
          </div>
        );
      },
    })),
  ];

  // Present employees columns
  const presentColumns = [
    { title: 'Emp ID', dataIndex: 'emp_id', key: 'emp_id', width: 100 },
    { title: 'Name', dataIndex: 'name', key: 'name', width: 200 },
    {
      title: 'Check In',
      dataIndex: 'first_in',
      key: 'first_in',
      width: 150,
      render: (text, record) => (
        <TimePicker
          value={text ? dayjs(text, 'HH:mm:ss') : null}
          format="HH:mm:ss"
          onChange={(time) => updateAttendanceField(record.id, 'first_in', time ? time.format('HH:mm:ss') : '')}
          disabled={!hasUpdate}
          size="small"
        />
      ),
    },
    {
      title: 'Check Out',
      dataIndex: 'last_out',
      key: 'last_out',
      width: 150,
      render: (text, record) => (
        <TimePicker
          value={text ? dayjs(text, 'HH:mm:ss') : null}
          format="HH:mm:ss"
          onChange={(time) => updateAttendanceField(record.id, 'last_out', time ? time.format('HH:mm:ss') : '')}
          disabled={!hasUpdate}
          size="small"
        />
      ),
    },
    {
      title: 'Hours Worked',
      dataIndex: 'total_hours_worked',
      key: 'total_hours_worked',
      width: 120,
      render: (text) => text || 0,
    },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 100 },
  ];

  // Absent employees columns
  const absentColumns = [
    { title: 'Emp ID', dataIndex: 'emp_id', key: 'emp_id', width: 100 },
    { title: 'Name', dataIndex: 'name', key: 'name', width: 200 },
    {
      title: 'Status',
      key: 'status',
      render: () => <Text type="danger">Absent</Text>,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={3}>Attendance Manager</Title>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Text strong>Month:</Text>
            <Select
              value={selectedMonth}
              onChange={setSelectedMonth}
              style={{ width: '100%', marginTop: 8 }}
            >
              {MONTHS.map((month) => (
                <Select.Option key={month} value={month}>
                  {month}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <Text strong>Year:</Text>
            <Select
              value={selectedYear}
              onChange={setSelectedYear}
              style={{ width: '100%', marginTop: 8 }}
            >
              {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map((year) => (
                <Select.Option key={year} value={year}>
                  {year}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <Text strong>Period:</Text>
            <div style={{ marginTop: 8 }}>
              <Text type="danger">{startDate}</Text>
              <Text> to </Text>
              <Text type="danger">{endDate}</Text>
            </div>
          </Col>
          <Col span={4}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={fetchAttendanceCounts}
              loading={loading}
              style={{ marginTop: 24 }}
            >
              Search
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Calendar Table */}
      {organization && (
        <>
          <Card title="Employee-wise Attendance Calendar" style={{ marginBottom: 16 }}>
            <div style={{ overflowX: 'auto', maxHeight: '300px', overflowY: 'auto' }}>
              <Table
                columns={calendarColumns}
                dataSource={employees}
                rowKey="id"
                pagination={false}
                loading={loading}
                scroll={{ x: 'max-content' }}
                size="small"
              />
            </div>
          </Card>

          {/* Details Section */}
          {selectedDate && (
            <>
              <Card style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Space>
                      <Text strong>Present Records:</Text>
                      <Input disabled value={filteredPresent.length} style={{ width: 60 }} />
                      <Text strong>Selected Date:</Text>
                      <Input disabled value={selectedDate} style={{ width: 120 }} />
                      <Text strong>Search:</Text>
                      <Input
                        value={searchPresent}
                        onChange={(e) => setSearchPresent(e.target.value)}
                        style={{ width: 150 }}
                        placeholder="Search present"
                      />
                    </Space>
                  </Col>
                  <Col span={12}>
                    <Space>
                      <Text strong>Absent Records:</Text>
                      <Input disabled value={filteredAbsent.length} style={{ width: 60 }} />
                      <Text strong>Search:</Text>
                      <Input
                        value={searchAbsent}
                        onChange={(e) => setSearchAbsent(e.target.value)}
                        style={{ width: 150 }}
                        placeholder="Search absent"
                      />
                    </Space>
                  </Col>
                </Row>
              </Card>

              <Row gutter={16}>
                {/* Present Employees */}
                <Col span={12}>
                  <Card title="Present Employees" size="small">
                    <Spin spinning={loadingDay}>
                      <Table
                        columns={presentColumns}
                        dataSource={filteredPresent}
                        rowKey="id"
                        pagination={false}
                        size="small"
                        scroll={{ y: 300 }}
                      />
                    </Spin>
                  </Card>
                </Col>

                {/* Absent Employees */}
                <Col span={12}>
                  <Card title="Absent Employees" size="small">
                    <Spin spinning={loadingDay}>
                      <Table
                        columns={absentColumns}
                        dataSource={filteredAbsent}
                        rowKey={(record, index) => index}
                        pagination={false}
                        size="small"
                        scroll={{ y: 300 }}
                      />
                    </Spin>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </>
      )}

      {!organization && (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text type="secondary">Please select an organization to view attendance</Text>
          </div>
        </Card>
      )}
    </div>
  );
};

export default AttendanceManager;
